import React, { FC } from 'react';
import { Dropdown } from 'rsuite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, IconDefinition, faDisplay, faArrowRotateRight, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import {
	changeDisputeQueueParams, fetchDisputeQueues,
} from '../../../../store/disputes/actions/dispute-queues';
import { useDisputesContext } from '../../../../store/disputes/disputes-context';
import { TablesNames } from '../../../../store/tables/tables-context';
import { Table } from '../../../table/Table';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useMerchants } from '../../../../store/merchnts/merchants-context';
import { useLocation } from 'react-router';
import { parseSearchParams } from '../../../../utils/utilities';
import { IDisputeQueuesFilters } from '../../DisputeQueuesFilterSection';

export enum DISPUTE_QUEUE_ACTION_NAMES {
	RETRY = 'Retry',
	VIEW = 'View',
	EDIT = 'Edit',
	PIN = 'Pin',
	UNPIN = 'Unpin',
	DELETE = 'Delete',
}

export interface IDropdownItems {
	name: string,
	picture: IconDefinition,
	action: (value1?: any, value2?: any, value3?: any) => void
}


interface IDisputeQueuesTableProps {
	listener: (params: any) => void,
	openRetryModal?: (id: string) => void,
	openDeleteModal?: (isOpen: boolean, id: string) => void,
	openEditQueueModal?: (queueId: string) => void,
}

export const DisputeQueuesTable: FC<IDisputeQueuesTableProps> = (props) => {
	const { listener, openRetryModal, openDeleteModal, openEditQueueModal } = props;
	const navigate = useNavigate();
	const { search } = useLocation();

	const { merchantState: { isWhitelabelPartnersListSelectorLoading, isPartnerNamesListLoading }} = useMerchants();

	const { disputesContextState, dispatch } = useDisputesContext();
	const {
		disputeQueues,
		disputeQueueTotalItemCount,
		isDisputeQueuesLoading,
	} = disputesContextState;

	const filtersFromUrl: IDisputeQueuesFilters = parseSearchParams(search) || {};

	const setFavoriteQueue = async (id: string, name: string, isFavorite: boolean): Promise<void> => {
		try {
			const body = { name, isPined: !isFavorite };
			await changeDisputeQueueParams(id, body);
			await fetchDisputeQueues({ dispatch, searchParams: filtersFromUrl });
		} catch (e) {
			console.log('setFavoriteQueue error', e.response);
		}
	};

	const dropDownItems = [
		{
			name: DISPUTE_QUEUE_ACTION_NAMES.VIEW,
			picture: faDisplay,
			action: (id: string): void => navigate(`/disputes/queue/${id}`, { state: { disputeQueuesSearch: search }})
		},
		{
			name: DISPUTE_QUEUE_ACTION_NAMES.EDIT,
			picture: faEdit,
			action: openEditQueueModal
		},
		{
			name: DISPUTE_QUEUE_ACTION_NAMES.PIN,
			picture: faThumbtack,
			action: setFavoriteQueue
		},
		{
			name: DISPUTE_QUEUE_ACTION_NAMES.UNPIN,
			picture: faThumbtack,
			action: setFavoriteQueue
		},
		{
			name: DISPUTE_QUEUE_ACTION_NAMES.DELETE,
			picture: faTrash,
			action: openDeleteModal
		},
		{
			name: DISPUTE_QUEUE_ACTION_NAMES.RETRY,
			picture: faArrowRotateRight,
			action: openRetryModal
		},
	];

	const rowActions = (rowData): JSX.Element => {
		const filteredDropDownItems =
			dropDownItems?.filter(el =>
				el?.name === DISPUTE_QUEUE_ACTION_NAMES.VIEW ||
				rowData?.original?.isPined && el?.name === DISPUTE_QUEUE_ACTION_NAMES.UNPIN ||
				!rowData?.original?.isPined && el?.name === DISPUTE_QUEUE_ACTION_NAMES.PIN ||
				rowData?.original?.name !== 'KountKCM' && el?.name == DISPUTE_QUEUE_ACTION_NAMES.DELETE ||
				rowData?.original?.name !== 'KountKCM' && el?.name == DISPUTE_QUEUE_ACTION_NAMES.EDIT ||
				rowData?.original?.errorCount && el?.name == DISPUTE_QUEUE_ACTION_NAMES.RETRY
			);

		return (
			<div className={clsx(rowData?.original?.isPined && 'dispute-queue-is-favorite')}>
				<Dropdown
					noCaret
					title='Actions'
					placement="bottomStart"
					className="user-dropdown dispute-dropdown fs-7 main-font-family"
					// disabled={isWorkflowsLoading}
				>
					{
						filteredDropDownItems?.map(({ name, picture, action }: IDropdownItems) => {
							const handleClick = (data: any): void => {
								switch (name) {
									case DISPUTE_QUEUE_ACTION_NAMES.RETRY:
										action(data?.id);
										break;
									case DISPUTE_QUEUE_ACTION_NAMES.EDIT:
										action(data?.id);
										break;
									case DISPUTE_QUEUE_ACTION_NAMES.UNPIN:
									case DISPUTE_QUEUE_ACTION_NAMES.PIN:
										action(data?.id, data?.name, data?.isPined);
										break;
									case DISPUTE_QUEUE_ACTION_NAMES.DELETE:
										action(true, data?.id);
										break;
									case DISPUTE_QUEUE_ACTION_NAMES.VIEW:
										action(data?.id);
										break;
									default:
										action();
										break;

								}
							};
							return (
								<Dropdown.Item
									key={name}
									onClick={(): void => handleClick(rowData?.original)}
									icon={<FontAwesomeIcon icon={picture}/>}
									className={clsx('fw-bolder fs-7', name === DISPUTE_QUEUE_ACTION_NAMES.RETRY ? 'text-danger' : 'text-muted')}
								>
									{' '}{name}
								</Dropdown.Item>
							);
						})
					}
				</Dropdown>
			</div>
		);
	};

	return (
		<div className="d-flex flex-wrap align-items-center dispute-queue-navigation">
			<Table
				name={TablesNames.disputesQueueTableData}
				data={disputeQueues ?? []}
				totalItemCount={disputeQueueTotalItemCount}
				isLoading={isDisputeQueuesLoading || isWhitelabelPartnersListSelectorLoading || isPartnerNamesListLoading}
				enableRowActions={true}
				rowActions={rowActions}
				actionColumnsSize={10}
				queryParamsListener={listener}
			/>
		</div>
	);
};
